import type { FlowsheetDefinition, FlowsheetColumn } from './definitions';
export interface CustomObservationDefinition {
  event_type: `CUSTOM.${string}`;
  version: number;
  label: string;
  subject: 'MOTHER' | 'FETUS';
  value_type: 'string' | 'number' | 'boolean' | 'enum';
  unit?: string | null;
  choices: string[];
  suggested_value?: string | number | boolean | null;
  enabled: boolean;
}
export const customColumnKey = (d: CustomObservationDefinition) => `${d.event_type}@${d.version}`;
export function customValueIssues(d: CustomObservationDefinition, value: unknown): string[] {
  const valid =
    d.value_type === 'number'
      ? typeof value === 'number' && Number.isFinite(value)
      : d.value_type === 'boolean'
        ? typeof value === 'boolean'
        : d.value_type === 'enum'
          ? typeof value === 'string' && d.choices.includes(value)
          : typeof value === 'string' && !!value.trim() && value.length <= 4000;
  return valid ? [] : [`${d.label}: enter a valid ${d.value_type} value.`];
}
export function customDefinitionIssues(defs: CustomObservationDefinition[]): string[] {
  const errors: string[] = [];
  const identities = new Set<string>();
  const enabled = new Set<string>();
  for (const d of defs) {
    const key = customColumnKey(d);
    if (
      !/^CUSTOM\.[A-Z][A-Z0-9_]{0,60}$/.test(d.event_type) ||
      !Number.isInteger(d.version) ||
      d.version < 1 ||
      d.version > 10000 ||
      !d.label.trim() ||
      d.label.length > 100
    )
      errors.push('Custom observation requires a valid identifier, version and label.');
    if (identities.has(key)) errors.push('Duplicate custom schema version.');
    identities.add(key);
    if (d.enabled && enabled.has(d.event_type)) errors.push('Enable only one version per custom observation.');
    if (d.enabled) enabled.add(d.event_type);
    if (
      d.value_type === 'enum' &&
      (!d.choices.length ||
        new Set(d.choices).size !== d.choices.length ||
        d.choices.some((c) => !c.trim() || c.length > 200))
    )
      errors.push('Enum choices must be nonblank and unique.');
    if (d.value_type !== 'enum' && d.choices.length) errors.push('Choices require an enum field.');
    if (d.unit && d.value_type !== 'number') errors.push('Units require a numeric field.');
    if (d.suggested_value !== undefined && d.suggested_value !== null)
      errors.push(...customValueIssues(d, d.suggested_value));
  }
  return errors;
}
export function withCustomColumns(
  base: FlowsheetDefinition,
  defs: CustomObservationDefinition[],
  labels: Record<string, string> = {},
): FlowsheetDefinition {
  const custom: FlowsheetColumn[] = defs.map((d) => ({
    key: customColumnKey(d),
    label: `${d.label}${d.unit ? ` (${d.unit})` : ''} · v${d.version}`,
    eventType: d.event_type,
    path: 'value',
    format: d.value_type === 'number' ? 'number' : 'text',
    subject: d.subject,
    schemaVersion: d.version,
    unit: d.unit ?? undefined,
  }));
  return { ...base, columns: [...base.columns, ...custom].map((c) => ({ ...c, label: labels[c.key] || c.label })) };
}
